import { useEffect, useRef, useState } from "react";
import axios from "axios";
import Globe from "react-globe.gl";
import * as THREE from "three";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Rotating 3D globe pinning each DJ to their broadcast city.
 * Every red pulse is a host. Click a pin -> jump to that DJ's profile.
 * Falls back gracefully when a host has no location.
 *
 * Broadcast lines: every ~4s we pick a random listener pin from
 * /api/visits/locations and animate a red signal arc from EVERY DJ to that
 * listener. Visualises "the signal reaches every corner".
 */
export default function DJWorldMap({ djs = [] }) {
  const containerRef = useRef(null);
  const globeRef = useRef(null);
  const signalTargetRef = useRef(null);
  const [width, setWidth] = useState(800);
  const [ready, setReady] = useState(false);
  const [hovered, setHovered] = useState(null);
  const [listeners, setListeners] = useState([]);
  const [signalTarget, setSignalTarget] = useState(null);

  // Only pin DJs with coordinates
  const pins = djs.filter((d) => typeof d.lat === "number" && typeof d.lng === "number");

  // Normalize country from "location" string. Anything explicitly mentioning
  // a non-US country wins; otherwise default to USA (most hosts are US-based
  // with state abbrevs like "Atlanta, GA" which we don't want to count as
  // countries).
  const countryFor = (loc = "") => {
    const s = loc.toLowerCase();
    if (s.includes("canada")) return "Canada";
    if (s.includes("ireland")) return "Ireland";
    if (s.includes("united kingdom") || s.includes(" uk")) return "United Kingdom";
    if (s.includes("south africa")) return "South Africa";
    return "USA";
  };
  const countries = new Set(pins.map((d) => countryFor(d.location)));

  // Responsive width
  useEffect(() => {
    if (!containerRef.current) return;
    const el = containerRef.current;
    const update = () => setWidth(el.clientWidth || 800);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Auto-rotate + tilt on ready
  useEffect(() => {
    if (!ready || !globeRef.current) return;
    const controls = globeRef.current.controls();
    if (controls) {
      controls.autoRotate = true;
      controls.autoRotateSpeed = 0.35;
      controls.enableZoom = false;
    }
    globeRef.current.pointOfView({ lat: 25, lng: -60, altitude: 2.2 }, 0);
  }, [ready]);

  // Fetch listener pins to power the broadcast-signal arcs. Refresh every
  // 90s so live pins stay reasonably fresh without hammering the API.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await axios.get(`${API}/visits/locations`);
        if (!cancelled) setListeners(res.data || []);
      } catch (e) {
        /* silent — arcs just won't render */
      }
    };
    load();
    const t = setInterval(load, 90 * 1000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, []);

  // Every 4s pick a new random listener as the "signal target" so arcs
  // continually reach new corners of the map. Prefer LIVE pins only when
  // there are enough of them to feel varied — otherwise fall back to the
  // full history pool so the animation always cycles through different
  // cities instead of getting stuck on the single online listener (usually
  // the viewer themselves).
  useEffect(() => {
    if (listeners.length === 0) {
      setSignalTarget(null);
      return;
    }
    const pickTarget = () => {
      const live = listeners.filter((l) => l.live);
      const pool = live.length >= 3 ? live : listeners;
      // Avoid picking the same target twice in a row when we can.
      let next = pool[Math.floor(Math.random() * pool.length)];
      if (pool.length > 1) {
        let guard = 0;
        while (
          next &&
          signalTargetRef.current &&
          next.lat === signalTargetRef.current.lat &&
          next.lng === signalTargetRef.current.lng &&
          guard < 5
        ) {
          next = pool[Math.floor(Math.random() * pool.length)];
          guard += 1;
        }
      }
      signalTargetRef.current = next;
      setSignalTarget(next);
    };
    pickTarget();
    const t = setInterval(pickTarget, 4000);
    return () => clearInterval(t);
  }, [listeners]);

  // Build arc data: from every DJ to the current signal target.
  const arcs =
    signalTarget && pins.length > 0
      ? pins.map((dj) => ({
          startLat: dj.lat,
          startLng: dj.lng,
          endLat: signalTarget.lat,
          endLng: signalTarget.lng,
        }))
      : [];

  const pauseRotate = () => {
    if (!globeRef.current) return;
    const c = globeRef.current.controls();
    if (c) c.autoRotate = false;
  };
  const resumeRotate = () => {
    if (!globeRef.current) return;
    const c = globeRef.current.controls();
    if (c) c.autoRotate = true;
  };

  const tooltip = (d) => {
    if (!d) return "";
    return `
      <div style="background:#000;border:1px solid #dc2626;padding:8px 12px;font-family:monospace;color:#fafafa;font-size:12px;line-height:1.4;text-align:left;min-width:180px;">
        <div style="font-size:9px;letter-spacing:.25em;color:#a1a1aa;text-transform:uppercase;margin-bottom:4px;">//_broadcaster</div>
        <div style="color:#fafafa;font-weight:bold;">${d.name || ""}</div>
        <div style="font-size:10px;color:#dc2626;letter-spacing:.15em;text-transform:uppercase;margin-top:2px;">${d.location || ""}</div>
        ${d.tagline ? `<div style="font-size:10px;color:#a1a1aa;margin-top:6px;line-height:1.3;">${d.tagline}</div>` : ""}
        <div style="font-size:9px;color:#71717a;letter-spacing:.2em;text-transform:uppercase;margin-top:6px;">CLICK PIN → PROFILE</div>
      </div>
    `;
  };

  return (
    <div data-testid="dj-world-map">
      {/* Header strip */}
      <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="text-xs tracking-[0.4em] text-zinc-300 uppercase mb-2">
            //_broadcast_from_anywhere
          </div>
          <h3 className="font-display text-2xl md:text-3xl text-zinc-50 leading-tight tracking-tight">
            EVERY PIN <span className="text-red-600">IS A HOST</span>.
          </h3>
        </div>
        <div className="grid grid-cols-2 gap-4 text-center" data-testid="dj-map-stats">
          <Stat label="HOSTS ON AIR" value={pins.length} accent="red" />
          <Stat label="COUNTRIES" value={countries.size} />
        </div>
      </div>

      {/* Globe */}
      <div
        ref={containerRef}
        onMouseEnter={pauseRotate}
        onMouseLeave={resumeRotate}
        className="relative border border-zinc-800 bg-black overflow-hidden"
        style={{ height: "min(520px, 65vw)" }}
        data-testid="dj-world-map-canvas"
      >
        <Globe
          ref={globeRef}
          width={width}
          height={Math.min(520, width * 0.65)}
          backgroundColor="rgba(0,0,0,0)"
          globeImageUrl="https://unpkg.com/three-globe@2.30.1/example/img/earth-night.jpg"
          showAtmosphere
          atmosphereColor="#dc2626"
          atmosphereAltitude={0.18}
          /* Static pin dots */
          pointsData={pins}
          pointLat={(d) => d.lat}
          pointLng={(d) => d.lng}
          pointColor={() => "#dc2626"}
          pointAltitude={0.02}
          pointRadius={0.55}
          pointLabel={tooltip}
          onPointClick={(d) => {
            if (d?.slug) window.location.href = `/djs/${d.slug}`;
          }}
          onPointHover={(d) => setHovered(d || null)}
          /* Pulsing rings on every host to make the globe feel alive */
          ringsData={pins}
          ringLat={(d) => d.lat}
          ringLng={(d) => d.lng}
          ringMaxRadius={2.2}
          ringPropagationSpeed={1.4}
          ringRepeatPeriod={1600}
          ringColor={() => (t) => `rgba(220, 38, 38, ${1 - t})`}
          /* Broadcast-signal arcs — every DJ pin fires a red thread toward
             the currently-selected listener target. Dashes give the "signal
             travelling" feel. */
          arcsData={arcs}
          arcStartLat={(d) => d.startLat}
          arcStartLng={(d) => d.startLng}
          arcEndLat={(d) => d.endLat}
          arcEndLng={(d) => d.endLng}
          arcColor={() => ["rgba(220, 38, 38, 0.15)", "rgba(255, 80, 80, 1)"]}
          arcStroke={0.55}
          arcDashLength={0.5}
          arcDashGap={0.35}
          arcDashInitialGap={() => Math.random()}
          arcDashAnimateTime={2000}
          arcAltitudeAutoScale={0.6}
          /* Invisible larger click layer so tiny points are easier to hit */
          customLayerData={pins}
          customLayerLat={(d) => d.lat}
          customLayerLng={(d) => d.lng}
          customThreeObject={() => {
            const g = new THREE.SphereGeometry(0.9, 12, 12);
            const m = new THREE.MeshBasicMaterial({
              color: 0xdc2626,
              transparent: true,
              opacity: 0,
            });
            return new THREE.Mesh(g, m);
          }}
          onGlobeReady={() => setReady(true)}
        />

        {/* Signal-lock indicator — top-right corner, shows the listener the
            broadcast arcs are currently reaching. Cycles every 4s. */}
        {signalTarget && (
          <div
            className="absolute top-3 right-3 border border-red-600 bg-black/85 backdrop-blur-sm px-3 py-2 pointer-events-none"
            data-testid="signal-lock"
          >
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-red-600 animate-pulse" />
              <div className="text-[9px] tracking-[0.3em] text-red-500 uppercase font-mono">
                //_signal_lock
              </div>
            </div>
            <div className="mt-1 text-xs text-zinc-50 font-mono max-w-[180px] truncate">
              {[signalTarget.city, signalTarget.country].filter(Boolean).join(", ") ||
                "Anonymous listener"}
            </div>
          </div>
        )}

        {/* Hover-info corner card (extra context beyond tooltip) */}
        {hovered && (
          <div className="absolute bottom-3 left-3 border border-red-600 bg-black/85 backdrop-blur-sm px-3 py-2 max-w-xs pointer-events-none">
            <div className="text-[10px] tracking-widest text-zinc-500 uppercase">
              //_now_hovering
            </div>
            <div className="font-display text-lg text-zinc-50 leading-tight">
              {hovered.name}
            </div>
            <div className="text-xs text-red-500 tracking-widest uppercase mt-0.5">
              {hovered.location}
            </div>
          </div>
        )}
      </div>

      <div className="mt-3 text-[10px] text-zinc-600 tracking-widest uppercase">
        //_red_arcs_are_the_signal_reaching_a_live_listener · click_a_pin_to_open_the_host_profile
      </div>
    </div>
  );
}

function Stat({ label, value, accent }) {
  const color = accent === "red" ? "text-red-600" : "text-zinc-50";
  return (
    <div className="border border-zinc-800 px-3 py-2 min-w-[110px] bg-zinc-900/40">
      <div className={`font-display text-2xl md:text-3xl leading-none ${color}`}>
        {value}
      </div>
      <div className="text-[9px] tracking-[0.25em] text-zinc-400 uppercase mt-1">
        {label}
      </div>
    </div>
  );
}
